import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { Modal, Box, Button, TextField, CircularProgress, Slider, Typography } from "@mui/material";
import FilestoreBrowser from "./FilestoreBrowser";
import WidthSelectionModal, { WidthConfig } from "./WidthSelectionModal";
import { applyWidthConfig, LINE_GEOMETRY_TYPES } from "../utils/geojsonUtils";
import { FilestoreFile, parseFilestoreFile, analyzeFileForLines } from "../api/filestore";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import { Icon } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Checkbox from "@mui/material/Checkbox";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  DrawingContext,
  DrawingMode,
  DrawingFeatureType,
} from "../contexts/DrawingContext";
import { useAddTreatment, useUpdateTreatment } from "../api/treatments";
import { UploadEndDateConfig } from "../types/treatment";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useProject, useProjectDefaultTreatmentCosts } from "../api/project";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { useTimezone } from "../contexts/TimezoneContext";
import {
  toDisplayCostPerArea,
  toDisplayCostPerDistance,
  toMetricCostPerArea,
  toMetricCostPerDistance,
} from "../utils/units";
import { toDisplayFuelLoad, toMetricFuelLoad } from "../utils/units";
import { feetToMeters, metersToFeet } from "../utils/units";
import { parseInTimezone } from "../utils/datetime";

dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

export {
  PRESCRIBED_BURN_TREATMENT,
  REVEGETATION_TREATMENT,
  GRAZING_TREATMENT,
  PRUNING_TREATMENT,
  UNDERSTORY_THINNING_TREATMENT,
  MINERAL_EARTH_TREATMENT,
  CHEMICAL_RETARDANT_TREATMENT,
  TREATMENT_TYPES,
  TREATMENT_CONFIGS,
  DEFAULT_TREATMENT_CONFIG,
} from "../constants/treatmentTypes";
export type { TreatmentConfig } from "../constants/treatmentTypes";
import {
  PRESCRIBED_BURN_TREATMENT,
  REVEGETATION_TREATMENT,
  PRUNING_TREATMENT,
  UNDERSTORY_THINNING_TREATMENT,
  SILVICULTURE_TREATMENT,
  CHEMICAL_RETARDANT_TREATMENT,
  TREATMENT_TYPES,
  TREATMENT_CONFIGS,
  DEFAULT_TREATMENT_CONFIG,
} from "../constants/treatmentTypes";
import { useRetardantProducts } from "../api/retardantProducts";
import AddRetardantProductDialog from "./AddRetardantProductDialog";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SilvicultureLabModal from "./SilvicultureLabModal";

const collectGeojsonPropertyFields = (data: any): string[] => {
  if (!data) {
    return [];
  }

  const collections = Array.isArray(data) ? data : [data];
  const keys = new Set<string>();

  collections.forEach((collection) => {
    if (collection?.features && Array.isArray(collection.features)) {
      collection.features.forEach((feature: any) => {
        if (feature?.properties && typeof feature.properties === "object") {
          Object.keys(feature.properties).forEach((key) => keys.add(key));
        }
      });
    }
  });

  return Array.from(keys).sort((a, b) => a.localeCompare(b));
};

const collectGeojsonFieldSamples = (
  data: any,
  field: string,
  limit: number = 3
): string[] => {
  if (!data || !field) {
    return [];
  }

  const collections = Array.isArray(data) ? data : [data];
  const samples: string[] = [];
  const seen = new Set<string>();

  for (const collection of collections) {
    if (!collection?.features || !Array.isArray(collection.features)) {
      continue;
    }
    for (const feature of collection.features) {
      const value = feature?.properties?.[field];
      if (value === undefined || value === null) {
        continue;
      }
      const asString = String(value);
      if (!seen.has(asString)) {
        seen.add(asString);
        samples.push(asString);
        if (samples.length >= limit) {
          return samples;
        }
      }
    }
  }

  return samples;
};

export interface TreatmentData {
  treatmentId: string | number;
  name: string;
  type: string;
  residualFuel: number;
  startDate: string;
  endDate: string;
  vegClass: string;
  vegDate: string;
  width: number;
  costPerUnit?: number;
  elevatedFuelReductionPct?: number;
  surfaceFuelReductionPct?: number;
  removeSlash?: boolean;
  cbhRaiseM?: number;
  cbdReductionPct?: number;
  canopyHeightReductionPct?: number;
  retardantProduct?: string;
  coverageFraction?: number;
}

interface TreatmentModalProps {
  mode: "add" | "edit";
  initialData?: Partial<TreatmentData>;
  onClose?: () => void;
}

interface FormFieldProps {
  icon: string;
  children: React.ReactNode;
}

const FormField = ({ icon, children }: FormFieldProps) => (
    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "baseline", mb: 2 }}>
      <Icon sx={{ m: 1, color: "#00d4ad" }}>{icon}</Icon>
      {children}
    </Box>
);

const AddEditTreatmentModal = ({
  mode,
  initialData,
  onClose,
}: TreatmentModalProps) => {
  const { project } = useProject();
  const unitSystem = useUnitSystem();
  const { selectedTimezone } = useTimezone();
  const [open, setOpen] = useState(false);
  const { mutateAsync: addTreatment } = useAddTreatment(project?.data?.id!);
  const { mutateAsync: updateTreatment } = useUpdateTreatment(
    project?.data?.id!,
    initialData?.treatmentId!
  );
  const [name, setName] = useState<string>("");
  const [fuel, setFuel] = useState(0);
  const today = dayjs().tz(selectedTimezone);
  const [endDate, setEndDate] = useState(today);
  const [vegClass, setVegClass] = useState("");
  const [vegDate, setVegDate] = useState(today);
  const [startDate, setStartDate] = useState(today);
  const [errors, setErrors] = useState<{
    name?: string;
    fuel?: string;
    vegClass?: string;
    width?: string;
    schedule?: string;
    repeatInterval?: string;
    cost?: string;
    uploadEndDateField?: string;
    uploadEndDateFormat?: string;
    elevatedFuelReductionPct?: string;
    surfaceFuelReductionPct?: string;
    cbhRaiseM?: string;
    cbdReductionPct?: string;
    retardantProduct?: string;
  }>({});
  const [type, setType] = useState(PRESCRIBED_BURN_TREATMENT);
  const [width, setWidth] = useState(10);
  const [costPerUnit, setCostPerUnit] = useState<string>("");
  const [costTouched, setCostTouched] = useState(false);
  const [elevatedFuelReductionPct, setElevatedFuelReductionPct] = useState<number>(0);
  const [surfaceFuelReductionPct, setSurfaceFuelReductionPct] = useState<number>(0);
  const [removeSlash, setRemoveSlash] = useState(false);
  const [cbhRaiseM, setCbhRaiseM] = useState<number>(2);
  const [cbdReductionPct, setCbdReductionPct] = useState<number>(30);
  const [labOpen, setLabOpen] = useState(false);
  // Products are a FiSci-curated catalog enabled per tenant, so the list is
  // fetched rather than hard-coded and may be empty for this organisation.
  const { data: retardantProducts = [] } = useRetardantProducts();
  const [retardantProduct, setRetardantProduct] = useState<string>("");
  const [addProductOpen, setAddProductOpen] = useState(false);
  const ADD_PRODUCT_VALUE = "__add_retardant_product__";
  // Percent in the UI (a slider reads better than 0..1); converted on submit.
  const [coveragePct, setCoveragePct] = useState<number>(100);
  const isSilviculture = type === SILVICULTURE_TREATMENT;
  const isRetardant = type === CHEMICAL_RETARDANT_TREATMENT;
  const {
    drawingMode,
    drawingFeatureType,
    setDrawingMode,
    setDrawingFeatureType,
    geojson,
    setGeojson,
  } = useContext(DrawingContext);
  const [fileError, setFileError] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [scheduleType, setScheduleType] = useState<"one-off" | "recurring">(
    "one-off"
  );
  const [scheduleStartDate, setScheduleStartDate] = useState(today);
  const [scheduleEndDate, setScheduleEndDate] = useState(today);
  const [repeatUnit, setRepeatUnit] = useState<"months" | "years">("months");
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [uploadEndDateMode, setUploadEndDateMode] = useState<"fixed" | "field">(
    "fixed"
  );
  const [uploadEndDateField, setUploadEndDateField] = useState("");
  const [uploadEndDateFormat, setUploadEndDateFormat] = useState("");
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filestoreBrowserOpen, setFilestoreBrowserOpen] = useState(false);
  const [widthSelectionOpen, setWidthSelectionOpen] = useState(false);
  const [selectedFileForAnalysis, setSelectedFileForAnalysis] = useState<FilestoreFile | null>(null);
  const [availableFields, setAvailableFields] = useState<string[]>([]);
  const previousUnitSystemRef = useRef(unitSystem);
  const supportsScheduling =
    type === PRESCRIBED_BURN_TREATMENT ||
    type.startsWith("Mechanical") ||
    type === PRUNING_TREATMENT ||
    // Retardant efficacy decays on a half-life, so a maintenance cadence is
    // the normal way to use it: recurring applications give the sawtooth of
    // real-world reapplication, and the cost engine prices every pass.
    type === CHEMICAL_RETARDANT_TREATMENT;
  const isSchedulingEnabled = mode === "add" && supportsScheduling;
  const isRecurring = isSchedulingEnabled && scheduleType === "recurring";
  const uploadPropertyFields = useMemo(
    () => collectGeojsonPropertyFields(geojson),
    [geojson]
  );
  const isUploadFlow = drawingFeatureType === DrawingFeatureType.UPLOAD;
  const isUploadUsingFieldDate = isUploadFlow && uploadEndDateMode === "field";
  const uploadFieldDateSamples = useMemo(() => {
    if (!isUploadUsingFieldDate || !uploadEndDateField) {
      return [];
    }
    return collectGeojsonFieldSamples(geojson, uploadEndDateField, 3);
  }, [geojson, uploadEndDateField, isUploadUsingFieldDate]);

  useEffect(() => {
    setStartDate((current) => current.tz(selectedTimezone, true));
    setEndDate((current) => current.tz(selectedTimezone, true));
    setVegDate((current) => current.tz(selectedTimezone, true));
    setScheduleStartDate((current) => current.tz(selectedTimezone, true));
    setScheduleEndDate((current) => current.tz(selectedTimezone, true));
  }, [selectedTimezone]);

  const { data: defaultCostResponse } = useProjectDefaultTreatmentCosts(
    project?.data?.id
  );

  const defaultCostMap = useMemo(() => {
    if (!defaultCostResponse?.data) {
      return null;
    }
    return new Map(
      defaultCostResponse.data.map((item) => [item.treatment_type, item])
    );
  }, [defaultCostResponse]);

  const generateSeriesId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `series-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };


  const renderCostField = () => {
    const isLinear = drawingFeatureType === DrawingFeatureType.LINE;
    const costLabel = isLinear
      ? unitSystem === "imperial"
        ? "Cost per mi"
        : "Cost per km"
      : unitSystem === "imperial"
        ? "Cost per acre"
        : "Cost per ha";
    const unitSuffix = isLinear
      ? unitSystem === "imperial"
        ? "/mi"
        : "/km"
      : unitSystem === "imperial"
        ? "/acre"
        : "/ha";

    return (
      <FormField icon={"attach_money"}>
        <TextField
            label={costLabel}
            InputProps={{
              type: "number",
              endAdornment: <InputAdornment position="end">{unitSuffix}</InputAdornment>,
              inputProps: { min: 0, step: "any" },
            }}
            fullWidth
            value={costPerUnit}
            onChange={(e) => {
              setCostTouched(true);
              setCostPerUnit(e.target.value);
            }}
            error={!!errors.cost}
            helperText={errors.cost}
        />
      </FormField>
    );
  };

  useEffect(() => {
    if (!supportsScheduling) {
      setScheduleType("one-off");
    }
  }, [supportsScheduling]);

  // Default the retardant picker to the first product the tenant may use,
  // once the list has loaded and nothing (e.g. an edit) has set it already.
  useEffect(() => {
    if (!retardantProduct && retardantProducts.length > 0) {
      setRetardantProduct(retardantProducts[0].key);
    }
  }, [retardantProduct, retardantProducts]);

  // Open the Silviculture Lab automatically when the type is selected.
  useEffect(() => {
    if (type === SILVICULTURE_TREATMENT) {
      setLabOpen(true);
    }
  }, [type]);

  useEffect(() => {
    if (uploadEndDateMode === "field" && uploadPropertyFields.length === 0) {
      setUploadEndDateMode("fixed");
      setUploadEndDateField("");
      setUploadEndDateFormat("");
    }
  }, [uploadEndDateMode, uploadPropertyFields.length]);

  useEffect(() => {
    if (
      uploadEndDateMode === "field" &&
      uploadPropertyFields.length > 0 &&
      !uploadEndDateField
    ) {
      setUploadEndDateField(uploadPropertyFields[0]);
    }
  }, [uploadEndDateMode, uploadPropertyFields, uploadEndDateField]);

  useEffect(() => {
    if (!isUploadUsingFieldDate) {
      setUploadEndDateFormat("");
    }
  }, [isUploadUsingFieldDate]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && drawingMode === DrawingMode.TREATMENT) {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (mode === "add") {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [drawingMode, mode]);

  useEffect(() => {
    const previousUnitSystem = previousUnitSystemRef.current;
    if (previousUnitSystem === unitSystem) {
      return;
    }

    previousUnitSystemRef.current = unitSystem;

    if (!open) {
      return;
    }

    setFuel((currentFuel) => {
      if (!Number.isFinite(currentFuel)) {
        return currentFuel;
      }

      const metricFuelLoad = toMetricFuelLoad(currentFuel, previousUnitSystem);
      return toDisplayFuelLoad(metricFuelLoad, unitSystem);
    });

    setWidth((currentWidth) => {
      if (drawingFeatureType !== DrawingFeatureType.LINE) {
        return currentWidth;
      }
      if (!Number.isFinite(currentWidth)) {
        return currentWidth;
      }

      const metricWidth =
        previousUnitSystem === "imperial" ? feetToMeters(currentWidth) : currentWidth;
      return unitSystem === "imperial" ? metersToFeet(metricWidth) : metricWidth;
    });

    if (costTouched) {
      setCostPerUnit((currentCost) => {
        if (currentCost === "") {
          return currentCost;
        }

        const parsed = Number(currentCost);
        if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
          return currentCost;
        }

        const isLinear = drawingFeatureType === DrawingFeatureType.LINE;
        const metricCost = isLinear
          ? toMetricCostPerDistance(parsed, previousUnitSystem)
          : toMetricCostPerArea(parsed, previousUnitSystem);

        const displayCost = isLinear
          ? toDisplayCostPerDistance(metricCost, unitSystem)
          : toDisplayCostPerArea(metricCost, unitSystem);

        return String(displayCost);
      });
    }
  }, [costTouched, drawingFeatureType, open, unitSystem]);

  // Auto-open when Upload feature selected
  useEffect(() => {
    if (
      mode === "add" &&
      drawingMode === DrawingMode.TREATMENT &&
      drawingFeatureType === DrawingFeatureType.UPLOAD
    ) {
      setOpen(true);
    }
  }, [drawingMode, drawingFeatureType, mode]);

  useEffect(() => {
    if (initialData && mode === "edit") {
      setOpen(true);
      setName(initialData.name!);
      setType(initialData.type!);
      if (initialData.residualFuel !== undefined && initialData.residualFuel !== null) {
        setFuel(toDisplayFuelLoad(Number(initialData.residualFuel), unitSystem));
      }
      setElevatedFuelReductionPct(initialData.elevatedFuelReductionPct ?? 0);
      setSurfaceFuelReductionPct(initialData.surfaceFuelReductionPct ?? 0);
      setRemoveSlash(Boolean(initialData.removeSlash));
      setCbhRaiseM(initialData.cbhRaiseM ?? 2);
      setCbdReductionPct(initialData.cbdReductionPct ?? 30);
      setRetardantProduct(initialData.retardantProduct || "");
      setCoveragePct(
        initialData.coverageFraction !== undefined &&
          initialData.coverageFraction !== null
          ? Math.round(Number(initialData.coverageFraction) * 100)
          : 100
      );
      setStartDate(
        parseInTimezone(initialData.startDate, selectedTimezone) ?? dayjs().tz(selectedTimezone)
      );
      setEndDate(
        parseInTimezone(initialData.endDate, selectedTimezone) ?? dayjs().tz(selectedTimezone)
      );
      setVegClass(initialData.vegClass!);
      setVegDate(
        parseInTimezone(initialData.vegDate, selectedTimezone) ?? dayjs().tz(selectedTimezone)
      );
      if (initialData.width) {
        setWidth(unitSystem === "imperial" ? metersToFeet(initialData.width) : initialData.width);
        setDrawingFeatureType(DrawingFeatureType.LINE);
      }
      if (initialData.costPerUnit !== undefined && initialData.costPerUnit !== null) {
        const isLinear = Boolean(initialData.width);
        const metricValue = Number(initialData.costPerUnit);
        const displayValue = isLinear
          ? toDisplayCostPerDistance(metricValue, unitSystem)
          : toDisplayCostPerArea(metricValue, unitSystem);
        setCostPerUnit(String(displayValue));
      } else {
        setCostPerUnit("");
      }
      setCostTouched(true);
    }
  }, [initialData, mode, selectedTimezone, unitSystem]);

  useEffect(() => {
    if (mode === "edit" || costTouched) {
      return;
    }

    const defaults = defaultCostMap?.get(type);
    if (!defaults) {
      return;
    }

    const isLinear = drawingFeatureType === DrawingFeatureType.LINE;
    const defaultValue = isLinear
      ? defaults.cost_per_linear_km
      : defaults.cost_per_ha;

    if (defaultValue === undefined || defaultValue === null) {
      return;
    }

    const displayValue = isLinear
      ? toDisplayCostPerDistance(defaultValue, unitSystem)
      : toDisplayCostPerArea(defaultValue, unitSystem);

    setCostPerUnit(String(displayValue));
  }, [defaultCostMap, type, drawingFeatureType, costTouched, mode, unitSystem]);

  const handleFilestoreFileSelected = async (filestoreFile: FilestoreFile) => {
    setFileError("");
    setIsFileLoading(true);
    try {
      const geojson = await parseFilestoreFile(filestoreFile.id);
      setGeojson(geojson);
      setFileName(filestoreFile.original_filename);
      setUploadEndDateMode("fixed");
      setUploadEndDateField("");
      setUploadEndDateFormat("");
      setFileError("");

      // Analyze file for line features
      const analysis = await analyzeFileForLines(filestoreFile.id);
      if (analysis.hasLines) {
        setSelectedFileForAnalysis(filestoreFile);
        setAvailableFields(analysis.fields);
        setWidthSelectionOpen(true);
      }
    } catch (err) {
      console.error(err);
      setFileError("Failed to load file from filestore");
    } finally {
      setIsFileLoading(false);
    }
  };

  const handleWidthConfigApply = (config: WidthConfig) => {
    if (geojson) {
      setGeojson(applyWidthConfig(geojson, config, LINE_GEOMETRY_TYPES));
    }
  };

  const validate = () => {
    const newErrors: {
      name?: string;
      fuel?: string;
      vegClass?: string;
      width?: string;
      schedule?: string;
      repeatInterval?: string;
      cost?: string;
      uploadEndDateField?: string;
      uploadEndDateFormat?: string;
      elevatedFuelReductionPct?: string;
      surfaceFuelReductionPct?: string;
      cbhRaiseM?: string;
      cbdReductionPct?: string;
      retardantProduct?: string;
    } = {};

    if (!name) {
      newErrors.name = "Treatment Name is required";
    }

    if (type === REVEGETATION_TREATMENT) {
      if (!vegClass) {
        newErrors.vegClass = "Vegetation Type is required";
      }
    } else if (type !== PRUNING_TREATMENT && type !== UNDERSTORY_THINNING_TREATMENT) {
      if (isNaN(fuel) || fuel < 0) {
        newErrors.fuel = "Residual Fuel should be a positive number or zero";
      }
    }

    if (isRetardant && !retardantProduct) {
      newErrors.retardantProduct = "Choose a retardant product";
    }

    if (drawingFeatureType === DrawingFeatureType.UPLOAD && !fileName) {
      newErrors.name = "Please upload a file";
    }

    if (isUploadUsingFieldDate) {
      if (uploadPropertyFields.length === 0) {
        newErrors.uploadEndDateField =
          "No properties found in the uploaded file";
      } else if (!uploadEndDateField) {
        newErrors.uploadEndDateField =
          "Select a property to use for the end date";
      }

      if (!uploadEndDateFormat) {
        newErrors.uploadEndDateFormat = "Enter the date format for the selected field";
      }
    }

    if (drawingFeatureType === DrawingFeatureType.LINE) {
      if (!width || isNaN(width) || width <= 0) {
        newErrors.width = "Width should be a positive number";
      }
    }

    if (costPerUnit === "") {
      newErrors.cost = "Cost is required";
    } else {
      const parsedCost = Number(costPerUnit);
      if (Number.isNaN(parsedCost) || parsedCost < 0) {
        newErrors.cost = "Cost must be zero or positive";
      }
    }

    if (isRecurring) {
      if (scheduleEndDate.isBefore(scheduleStartDate, "day")) {
        newErrors.schedule =
          "Schedule end date must be on or after start date";
      }
      if (isNaN(repeatInterval) || repeatInterval <= 0) {
        newErrors.repeatInterval = "Interval must be greater than zero";
      }
    }

    if (type === UNDERSTORY_THINNING_TREATMENT) {
      if (
        !Number.isFinite(elevatedFuelReductionPct) ||
        elevatedFuelReductionPct < 0 ||
        elevatedFuelReductionPct > 100
      ) {
        newErrors.elevatedFuelReductionPct =
          "Elevated fuel reduction must be between 0 and 100";
      }
      if (
        !Number.isFinite(surfaceFuelReductionPct) ||
        surfaceFuelReductionPct < 0 ||
        surfaceFuelReductionPct > 100
      ) {
        newErrors.surfaceFuelReductionPct =
          "Surface fuel reduction must be between 0 and 100";
      }
    }

    if (isSilviculture) {
      const pct = (v: number) => Number.isFinite(v) && v >= 0 && v <= 100;
      if (!pct(surfaceFuelReductionPct)) {
        newErrors.surfaceFuelReductionPct =
          "Surface fuel reduction must be between 0 and 100";
      }
      if (!pct(cbdReductionPct)) {
        newErrors.cbdReductionPct =
          "Thinning must be between 0 and 100";
      }
      if (!Number.isFinite(cbhRaiseM) || cbhRaiseM < 0 || cbhRaiseM > 20) {
        newErrors.cbhRaiseM = "Canopy base raise must be between 0 and 20 m";
      }
    }

    return newErrors;
  };

  const reset = () => {
    setName("");
    setFuel(0);
    setEndDate(dayjs().tz(selectedTimezone));
    setVegClass("");
    setVegDate(dayjs().tz(selectedTimezone));
    setWidth(10);
    setCostPerUnit("");
    setCostTouched(false);
    setScheduleType("one-off");
    setScheduleStartDate(dayjs().tz(selectedTimezone));
    setScheduleEndDate(dayjs().tz(selectedTimezone));
    setRepeatUnit("months");
    setRepeatInterval(1);
    setErrors({});
    setType(PRESCRIBED_BURN_TREATMENT);
    setElevatedFuelReductionPct(0);
    setSurfaceFuelReductionPct(0);
    setRemoveSlash(false);
    setCbhRaiseM(2);
    setCbdReductionPct(30);
    setRetardantProduct("");
    setCoveragePct(100);
    setDrawingMode(DrawingMode.NONE);
    setDrawingFeatureType(DrawingFeatureType.NONE);
    setUploadEndDateMode("fixed");
    setUploadEndDateField("");
    setUploadEndDateFormat("");
    setIsFileLoading(false);
    setIsSubmitting(false);
  };

  const renderUploadEndDateOptions = () => {
    if (!isUploadFlow) {
      return null;
    }

    return (
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <Icon sx={{ m: 1, color: "#00d4ad" }}>event</Icon>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={uploadEndDateMode}
              onChange={(e) =>
                setUploadEndDateMode(e.target.value as "fixed" | "field")
              }
            >
              <FormControlLabel
                value="fixed"
                control={<Radio />}
                label="Manually select date for all features"
              />
              <FormControlLabel
                value="field"
                control={<Radio />}
                label="Use a date field from the uploaded features"
                disabled={uploadPropertyFields.length === 0}
              />
              {uploadEndDateMode === "field" && (
                <>
                  <FormControl fullWidth sx={{ marginBottom: 1 }}>
                    <InputLabel id="upload-end-date-field-label">
                      Property field
                    </InputLabel>
                    <Select
                      labelId="upload-end-date-field-label"
                      label="Property field"
                      value={uploadEndDateField}
                      onChange={(e) => setUploadEndDateField(e.target.value)}
                    >
                      {uploadPropertyFields.map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {errors.uploadEndDateField && (
                    <FormHelperText error sx={{ marginBottom: 1 }}>
                      {errors.uploadEndDateField}
                    </FormHelperText>
                  )}
                  {uploadPropertyFields.length === 0 && (
                    <FormHelperText error>
                      No properties were found in the uploaded file.
                    </FormHelperText>
                  )}
                  {uploadFieldDateSamples.length > 0 ? (
                    <FormHelperText sx={{ marginBottom: 1 }}>
                      Sample values: {uploadFieldDateSamples.join(", ")}
                    </FormHelperText>
                  ) : (
                    <FormHelperText sx={{ marginBottom: 1 }}>
                      No date values detected for this field yet.
                    </FormHelperText>
                  )}
                  <TextField
                    label="Date format (e.g. DD/MM/YYYY)"
                    fullWidth
                    value={uploadEndDateFormat}
                    onChange={(e) => setUploadEndDateFormat(e.target.value)}
                    error={!!errors.uploadEndDateFormat}
                    helperText={
                      errors.uploadEndDateFormat ||
                      "Enter the format that matches the sample values"
                    }
                    sx={{ marginBottom: 1 }}
                  />
                </>
              )}
            </RadioGroup>
          </FormControl>
        </div>
      </div>
    );
  };

  const shouldShowRevegetationDate =
    type === REVEGETATION_TREATMENT && !isUploadUsingFieldDate;

  const renderScheduleFields = () => {
    if (
      !isSchedulingEnabled ||
      isUploadUsingFieldDate
    ) {
      return null;
    }

    return (
      <>
        <FormField icon="repeat">
          <FormControl fullWidth>
            <InputLabel id="schedule-type-label">Schedule</InputLabel>
            <Select
                labelId="schedule-type-label"
                value={scheduleType}
                label="Schedule"
                onChange={(e) =>
                    setScheduleType(e.target.value as "one-off" | "recurring")
                }
            >
              <MenuItem value="one-off">One-off</MenuItem>
              <MenuItem value="recurring">Recurring</MenuItem>
            </Select>
          </FormControl>
        </FormField>

        {isRecurring ? (
          <>
            <FormField icon="calendar_today">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="Schedule Start"
                    value={scheduleStartDate}
                    format="DD/MM/YYYY"
                    onChange={(value) => {
                      if (value) {
                        setScheduleStartDate(value.tz(selectedTimezone, true));
                      }
                    }}
                />
              </LocalizationProvider>
            </FormField>
            <FormField icon="event">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="Schedule End"
                    value={scheduleEndDate}
                    format="DD/MM/YYYY"
                    onChange={(value) => {
                      if (value) {
                        setScheduleEndDate(value.tz(selectedTimezone, true));
                      }
                    }}
                />
              </LocalizationProvider>
            </FormField>
            {errors.schedule && (
              <FormHelperText error>
                {errors.schedule}
              </FormHelperText>
            )}
            <FormField icon="update">
              <Box sx={{ display: "flex", gap: "12px", width: "100%", alignItems: "baseline" }}>
                <TextField
                    label="Repeat Interval"
                    InputProps={{
                      type: "number",
                      inputProps: { min: 1 },
                    }}
                    value={repeatInterval}
                    onChange={(e) => setRepeatInterval(parseInt(e.target.value, 10))}
                    error={!!errors.repeatInterval}
                    helperText={errors.repeatInterval}
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel id="repeat-unit-label">Units</InputLabel>
                  <Select
                      labelId="repeat-unit-label"
                      value={repeatUnit}
                      label="Units"
                      onChange={(e) =>
                          setRepeatUnit(e.target.value as "months" | "years")
                      }
                  >
                    <MenuItem value="months">Months</MenuItem>
                    <MenuItem value="years">Years</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </FormField>
          </>
        ) : (
            <FormField icon="calendar_month">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    label="Date"
                    value={endDate}
                    format="DD/MM/YYYY"
                    onChange={(value) => {
                      if (value) {
                        setEndDate(value.tz(selectedTimezone, true));
                      }
                    }}
                />
              </LocalizationProvider>
            </FormField>
        )}
      </>
    );
  };

  const handleClose = () => {
    reset();
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      try {
        const parsedCostPerUnit = Number(costPerUnit);
        const isLinear = drawingFeatureType === DrawingFeatureType.LINE;
        const metricCostPerUnit = isLinear
          ? toMetricCostPerDistance(parsedCostPerUnit, unitSystem)
          : toMetricCostPerArea(parsedCostPerUnit, unitSystem);

        const metricFuel = toMetricFuelLoad(Number(fuel), unitSystem);

        const metricWidth =
          drawingFeatureType === DrawingFeatureType.LINE && unitSystem === "imperial"
            ? feetToMeters(Number(width))
            : Number(width);

        const basePayload = () => {
          if (type === REVEGETATION_TREATMENT) {
            return {
              name: name,
              residualFuel: 0,
              type: type,
              vegClass: vegClass,
              vegDate: shouldShowRevegetationDate
                ? vegDate.tz(selectedTimezone, true).format("YYYY-MM-DD")
                : null,
              costPerUnit: metricCostPerUnit,
            };
          }
          if (type === "Grazing") {
            return {
              name: name,
              residualFuel: metricFuel,
              startDate: startDate.tz(selectedTimezone, true).format("YYYY-MM-DD"),
              endDate: endDate.tz(selectedTimezone, true).format("YYYY-MM-DD"),
              type: type,
              vegClass: null,
              vegDate: null,
              costPerUnit: metricCostPerUnit,
            };
          }
          if (isRetardant) {
            // A retardant changes how the fuel burns, not how much there is —
            // so no residual fuel, and the product/coverage ride along.
            const base = {
              name: name,
              residualFuel: 0,
              type: type,
              vegClass: null,
              vegDate: null,
              costPerUnit: metricCostPerUnit,
              retardantProduct: retardantProduct,
              coverageFraction: coveragePct / 100,
            };

            if (!isRecurring) {
              return {
                ...base,
                endDate: endDate.tz(selectedTimezone, true).format("YYYY-MM-DD"),
              };
            }

            return base;
          }
          if (
            type === PRUNING_TREATMENT ||
            type === UNDERSTORY_THINNING_TREATMENT ||
            isSilviculture
          ) {
            const base = {
              name: name,
              residualFuel: 0,
              type: type,
              vegClass: null,
              vegDate: null,
              costPerUnit: metricCostPerUnit,
              ...(type === UNDERSTORY_THINNING_TREATMENT
                ? {
                    elevatedFuelReductionPct,
                    surfaceFuelReductionPct,
                    removeSlash,
                  }
                : {}),
              ...(isSilviculture
                ? {
                    // Thinning (stem removal) drives both knobs in the sim:
                    // elevated stratum fuel load and canopy bulk density.
                    elevatedFuelReductionPct: cbdReductionPct,
                    surfaceFuelReductionPct,
                    removeSlash,
                    cbhRaiseM,
                    cbdReductionPct,
                    // Topping was dropped from the lab; send 0 so editing an
                    // older treatment clears any stored CH reduction.
                    canopyHeightReductionPct: 0,
                  }
                : {}),
            };

            if (!isRecurring) {
              return {
                ...base,
                endDate: endDate.tz(selectedTimezone, true).format("YYYY-MM-DD"),
              };
            }

            return base;
          }
          const base = {
            name: name,
            residualFuel: type === UNDERSTORY_THINNING_TREATMENT ? 0 : metricFuel,
            type: type,
            vegClass: null,
            vegDate: null,
            costPerUnit: metricCostPerUnit,
          };

          if (!isRecurring) {
            return {
              ...base,
              endDate: endDate.tz(selectedTimezone, true).format("YYYY-MM-DD"),
            };
          }

          return base;
        };

        const enrichedPayload = (payload: any) => {
          if (drawingFeatureType === DrawingFeatureType.LINE) {
            return {
              ...payload,
              width: metricWidth,
            };
          }
          return payload;
        };

        const buildScheduledPayloads = (seriesId?: string) => {
          const payload = enrichedPayload(basePayload());

          const applySeries = (data: any) =>
            seriesId ? { ...data, seriesId } : data;

          if (!isRecurring) {
            return [{ ...applySeries(payload), name: name }];
          }

          const occurrences: any[] = [];
          const start = scheduleStartDate.tz(selectedTimezone, true).startOf("day");
          const end = scheduleEndDate.tz(selectedTimezone, true).endOf("day");
          let cursor = start.clone();

          const advance = () => {
            cursor = cursor.add(
              repeatInterval,
              repeatUnit === "months" ? "month" : "year"
            );
          };

          while (cursor.isSameOrBefore(end, "day")) {
            const occurrenceDate = cursor.format("YYYY-MM-DD");
            const occurrencePayload: any = {
              ...applySeries(payload),
              name: `${name} (${occurrenceDate})`,
              endDate: occurrenceDate,
            };
            if (payload.startDate) {
              occurrencePayload.startDate = occurrenceDate;
            }
            occurrences.push({
              ...occurrencePayload,
            });
            advance();
          }

          return occurrences.length > 0
            ? occurrences
            : [applySeries(payload)];
        };

        const baseSeriesId = isRecurring ? generateSeriesId() : undefined;
        const uploadEndDateConfig: UploadEndDateConfig | undefined = isUploadFlow
          ? uploadEndDateMode === "fixed"
            ? { mode: "fixed", value: endDate.tz(selectedTimezone, true).format("YYYY-MM-DD") }
            : uploadEndDateMode === "field" && uploadEndDateField
            ? {
                mode: "field",
                field: uploadEndDateField,
                ...(uploadEndDateFormat
                  ? { format: uploadEndDateFormat }
                  : {}),
              }
            : undefined
          : undefined;

        const scheduledPayloads = isSchedulingEnabled
          ? buildScheduledPayloads(baseSeriesId)
          : [
              baseSeriesId
                ? { ...enrichedPayload(basePayload()), seriesId: baseSeriesId }
                : enrichedPayload(basePayload()),
            ];

        setIsSubmitting(true);
        for (const payload of scheduledPayloads) {
          if (mode === "add") {
            // For recurring uploads each occurrence has its own date on the
            // payload; the fixed uploadEndDateConfig otherwise forces every
            // occurrence onto the same date, so rebind its value per occurrence.
            const payloadUploadConfig =
              uploadEndDateConfig &&
              uploadEndDateConfig.mode === "fixed" &&
              payload.endDate
                ? { ...uploadEndDateConfig, value: payload.endDate }
                : uploadEndDateConfig;
            const payloadWithUploadConfig =
              payloadUploadConfig && isUploadFlow
                ? { ...payload, uploadEndDateConfig: payloadUploadConfig }
                : payload;
            await addTreatment({ ...payloadWithUploadConfig, geojsonOverride: geojson });
          } else {
            await updateTreatment(payload);
          }
        }

        handleClose();
      } catch (error) {
        console.error("Error adding treatment:", error);
        // Handle the error, e.g., display an error message to the user
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!open) return null;

  const config = TREATMENT_CONFIGS[type] || DEFAULT_TREATMENT_CONFIG;

  const isLine = drawingFeatureType === DrawingFeatureType.LINE;
  const showEndDate = !isSchedulingEnabled;
  const widthUnit = unitSystem === "imperial" ? "ft" : "m";
  const fuelUnit = unitSystem === "imperial" ? "tons/acre" : "t/ha";

  return (
    <>
      <Modal open={open}
             onClose={(_event, _reason) => {
               // Keep the modal open while parsing/submitting so a stray
               // backdrop click or Escape can't dismiss it mid-import.
               if (isFileLoading || isSubmitting) return;
               handleClose();
             }}
             sx={{
               display: "flex",
               justifyContent: "center",
               alignItems: "center",
               width: "100%",
               height: "100%",
               position: "absolute"
      }}>
        <Box sx={{
          padding: 2,
          borderRadius: 8,
          backgroundColor: "white",
          margin: "auto",
          top: "50%",
          left: "50%",
          width: "30%",
          position: "absolute",
          transform: "translate(-50%, -50%)",
          overflow: "hidden",
        }}>
          {/* Loading Overlay */}
          {(isFileLoading || isSubmitting) && <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255,255,255,0.8)",
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
          >
            <CircularProgress color="primary" />
            <span>
                {isFileLoading
                    ? "Parsing uploaded file..."
                    : "Submitting treatments..."}
              </span>
          </Box>}

          <h2>{mode === "edit" ? "Edit Treatment" : "Add New Treatment"}</h2>

          {/* Upload Section */}
          {drawingFeatureType === DrawingFeatureType.UPLOAD && (
              <Box sx={{ mb: 3 }}>
                <p style={{ fontSize: "0.85em", color: "#666", margin: "0 0 8px" }}>
                  Shapefiles must be uploaded as a .zip archive.
                </p>
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setFilestoreBrowserOpen(true)}
                    disabled={isFileLoading}
                  >
                    Browse My Files
                  </Button>
                  {fileName && (
                    <p style={{ fontSize: "0.9em", marginTop: 8 }}>Selected: {fileName}</p>
                  )}
                </Box>

                {fileError && (
                  <p style={{ color: "red", fontSize: "0.9em", marginTop: 8 }}>{fileError}</p>
                )}

                <FilestoreBrowser
                  open={filestoreBrowserOpen}
                  onClose={() => setFilestoreBrowserOpen(false)}
                  onSelectFile={handleFilestoreFileSelected}
                  acceptedFileTypes=".zip,.kml,.geojson,.json"
                />

                {selectedFileForAnalysis && (
                  <WidthSelectionModal
                    open={widthSelectionOpen}
                    onClose={() => setWidthSelectionOpen(false)}
                    onApply={handleWidthConfigApply}
                    file={selectedFileForAnalysis}
                    fields={availableFields}
                    geojson={geojson}
                  />
                )}
              </Box>
          )}
          {renderUploadEndDateOptions()}

          {/* Common Fields */}
          <FormField icon="label">
            <TextField
                label="Treatment Name"
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
            />
          </FormField>

          <FormField icon="category">
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Treatment Type
              </InputLabel>
              <Select
                  labelId="demo-simple-select-label"
                  value={type}
                  label="Treatment Type"
                  onChange={(e) => setType(e.target.value)}
              >
                {TREATMENT_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>

          {type === UNDERSTORY_THINNING_TREATMENT && (
            <>
              <FormField icon="tune">
                <TextField
                  fullWidth
                  label="Elevated fuel reduction %"
                  type="number"
                  value={elevatedFuelReductionPct}
                  onChange={(e) =>
                    setElevatedFuelReductionPct(Number(e.target.value))
                  }
                  InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
                  error={!!errors.elevatedFuelReductionPct}
                  helperText={errors.elevatedFuelReductionPct}
                />
              </FormField>

              <FormField icon="tune">
                <TextField
                  fullWidth
                  label="Surface fuel reduction %"
                  type="number"
                  value={surfaceFuelReductionPct}
                  onChange={(e) =>
                    setSurfaceFuelReductionPct(Number(e.target.value))
                  }
                  InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
                  error={!!errors.surfaceFuelReductionPct}
                  helperText={errors.surfaceFuelReductionPct}
                />
              </FormField>

              <FormField icon="grass">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={removeSlash}
                      onChange={(e) => setRemoveSlash(e.target.checked)}
                    />
                  }
                  label="Remove slash"
                />
              </FormField>
            </>
          )}

          {isSilviculture && (
            <Box sx={{ mb: 2, p: 1.5, border: "1px solid #e3e3e3", borderRadius: 2, bgcolor: "#fafafa" }}>
              <p style={{ margin: "0 0 6px", fontSize: "0.82em", color: "#555" }}>
                Combined thinning + pruning with bespoke canopy targets.
              </p>
              <p style={{ margin: "0 0 10px", fontSize: "0.78em", color: "#333", lineHeight: 1.6 }}>
                Thinning −{cbdReductionPct}% stems · −{surfaceFuelReductionPct}% surface
                {removeSlash ? " · remove slash" : ""}<br />
                Canopy CBH +{cbhRaiseM.toFixed(1)} m
              </p>
              <Button
                variant="outlined"
                startIcon={<Icon>forest</Icon>}
                onClick={() => setLabOpen(true)}
                sx={{ color: "#00a98c", borderColor: "#00d4ad" }}
              >
                Open Silviculture Lab
              </Button>
            </Box>
          )}

          {/* Conditional Fields based on Config */}
          {config.showVegClass && (
              <FormField icon="grass">
                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                  <InputLabel id="veg-class-select-label">
                    Vegetation Type
                  </InputLabel>
                  <Select
                      labelId="veg-class-select-label"
                      value={vegClass}
                      label="Vegetation Type"
                      onChange={(e) => setVegClass(e.target.value)}
                      error={!!errors.vegClass}
                  >
                    {project?.data?.veg_classes?.map((veg) => (
                        <MenuItem value={veg}>{veg}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FormField>
          )}

          {isLine && (
              <FormField icon="straighten">
                <TextField
                    fullWidth
                    label="Width"
                    type="number"
                    value={width}
                    onChange={(e) => setWidth(parseFloat(e.target.value))}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">{widthUnit}</InputAdornment>
                        ),
                      },
                    }}/>
              </FormField>
          )}

          {config.showFuel && (
              <FormField icon="scale">
                <TextField
                    fullWidth
                    label="Residual Fuel"
                    type="number"
                    value={fuel}
                    onChange={(e) => setFuel(parseFloat(e.target.value))}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">{fuelUnit}</InputAdornment>
                        ),
                      },
                    }} />
              </FormField>
          )}

          {config.showRetardantFields && (
            <>
              <FormField icon="science">
                <FormControl fullWidth error={!!errors.retardantProduct}>
                  <InputLabel id="retardant-product-label">Retardant Product</InputLabel>
                  <Select
                    labelId="retardant-product-label"
                    value={retardantProduct}
                    label="Retardant Product"
                    onChange={(e) => {
                      if (e.target.value === ADD_PRODUCT_VALUE) {
                        setAddProductOpen(true);
                        return;
                      }
                      setRetardantProduct(e.target.value);
                    }}
                  >
                    {retardantProducts.map((product) => (
                      <MenuItem key={product.key} value={product.key}>
                        {product.name}
                      </MenuItem>
                    ))}
                    {/* An edited treatment may reference a product since
                        retired or withdrawn from this tenant; keep it
                        selectable so the edit round-trips unchanged. */}
                    {retardantProduct &&
                      !retardantProducts.some((p) => p.key === retardantProduct) && (
                        <MenuItem value={retardantProduct}>
                          {retardantProduct} (no longer available)
                        </MenuItem>
                      )}
                    <Divider />
                    <MenuItem value={ADD_PRODUCT_VALUE}>
                      <ListItemIcon>
                        <Icon fontSize="small">add</Icon>
                      </ListItemIcon>
                      <ListItemText>Add retardant product…</ListItemText>
                    </MenuItem>
                  </Select>
                  {errors.retardantProduct && (
                    <FormHelperText>{errors.retardantProduct}</FormHelperText>
                  )}
                  {retardantProducts.length === 0 && (
                    <FormHelperText>
                      No retardant products yet — add one from the list.
                    </FormHelperText>
                  )}
                </FormControl>
              </FormField>

              <FormField icon="opacity">
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Coverage: {coveragePct}%
                  </Typography>
                  <Slider
                    size="small"
                    min={0}
                    max={100}
                    step={5}
                    value={coveragePct}
                    onChange={(_e, v) => setCoveragePct(v as number)}
                  />
                  <FormHelperText sx={{ m: 0 }}>
                    Fraction of the treated area the retardant actually lands
                    on. Efficacy scales linearly with coverage.
                  </FormHelperText>
                </Box>
              </FormField>
            </>
          )}

          {renderCostField()}

          {/* Date Logic */}
          {config.showStartDate && (
              <FormField icon="calendar_today">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(value) => {
                        if (value) {
                          setStartDate(value.tz(selectedTimezone, true));
                        }
                      }}
                      format="DD/MM/YYYY"
                  />
                </LocalizationProvider>
              </FormField>
          )}

          {(config.showVegDate && shouldShowRevegetationDate) && (
              <FormField icon="calendar_month">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                      label="Revegetation Date"
                      value={vegDate}
                      onChange={(value) => {
                        if (value) {
                          setVegDate(value.tz(selectedTimezone, true));
                        }
                      }}
                      format="DD/MM/YYYY"
                  />
                </LocalizationProvider>
              </FormField>
          )}

          {(showEndDate && !shouldShowRevegetationDate) && (
              <FormField icon="calendar_month">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(value) => {
                        if (value) {
                          setEndDate(value.tz(selectedTimezone, true));
                        }
                      }}
                      format="DD/MM/YYYY"
                  />
                </LocalizationProvider>
              </FormField>
          )}

          {renderScheduleFields()}

          <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              sx={{backgroundColor: "#00d4ad", color: "white" }}>
            Submit
          </Button>
        </Box>
      </Modal>

      <AddRetardantProductDialog
        open={addProductOpen}
        onClose={() => setAddProductOpen(false)}
        onCreated={(product) => setRetardantProduct(product.key)}
      />

      <SilvicultureLabModal
        open={labOpen && isSilviculture}
        onClose={() => setLabOpen(false)}
        thinningPct={cbdReductionPct}
        setThinningPct={setCbdReductionPct}
        surfaceFuelReductionPct={surfaceFuelReductionPct}
        setSurfaceFuelReductionPct={setSurfaceFuelReductionPct}
        removeSlash={removeSlash}
        setRemoveSlash={setRemoveSlash}
        cbhRaiseM={cbhRaiseM}
        setCbhRaiseM={setCbhRaiseM}
      />
    </>
  );
};

export default AddEditTreatmentModal;
